'use client'

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getNinxKeys } from '@/app/lib/auth-cookies'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Plus,
  Trash2,
  RefreshCw,
  ShoppingCart,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react'

// Types for our basket system
interface TradingPair {
  id: string
  symbol: string
  quantity: number
  currentPrice?: number
  usdtValue?: number
}

interface Basket {
  id: string
  title: string
  pairs: TradingPair[]
  createdAt: string
  totalUsdtRequired?: number
}

interface PriceData {
  [symbol: string]: {
    last_price: number
  }
}

/**
 * @uiComponent
 * @name Baskets
 * @description The Baskets component allows users to create trading baskets with multiple USDT pairs,
 * view real-time pricing, and execute bulk buy orders. Features include basket management,
 * real-time price updates, and one-click bulk execution using stored NINX API keys.
 * @location Baskets plugin in the trading terminal
 * @prop none - No external props, manages baskets in localStorage and fetches real-time data
 */
export default function Baskets() {
  // State management
  const [baskets, setBaskets] = useState<Basket[]>([])
  const [selectedBasket, setSelectedBasket] = useState<Basket | null>(null)
  const [isCreatingBasket, setIsCreatingBasket] = useState(false)
  const [newBasketTitle, setNewBasketTitle] = useState('')
  const [newPairSymbol, setNewPairSymbol] = useState('')
  const [newPairQuantity, setNewPairQuantity] = useState('')
  const [isExecuting, setIsExecuting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false)

  // Get symbols from selected basket for price fetching
  const symbols = selectedBasket?.pairs.map(pair => pair.symbol) || []
  
  // React Query for price fetching with automatic caching and refetching
  const { data: prices = {}, isLoading: isLoadingPrices, error: priceError, refetch: refetchPrices } = useQuery({
    queryKey: ['basket-prices', selectedBasket?.id, symbols],
    queryFn: async () => {
      if (symbols.length === 0) return {}
      
      // Get NINX API keys from storage
      const { apiKey, apiSecret } = getNinxKeys()
      
      if (!apiKey || !apiSecret) {
        throw new Error('NINX API keys not found. Please sign in to NINX first.')
      }

      // Ensure all symbols end with USDT
      const usdtSymbols = symbols.map(symbol => 
        symbol.toUpperCase().endsWith('USDT') ? symbol.toUpperCase() : `${symbol.toUpperCase()}USDT`
      )

      const response = await fetch('/api/connectors/ninx/ltp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey,
          apiSecret,
          symbols: usdtSymbols
        })
      })

      if (!response.ok) {
        throw new Error('Failed to fetch real-time prices')
      }

      return await response.json()
    },
    enabled: symbols.length > 0 && !!selectedBasket,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 2
  })

  // Load baskets from localStorage on component mount
  useEffect(() => {
    const loadBaskets = () => {
      try {
        const stored = localStorage.getItem('trading-baskets')
        if (stored) {
          const parsedBaskets = JSON.parse(stored)
          setBaskets(parsedBaskets)
          
          // Restore selected basket from localStorage
          const selectedBasketId = localStorage.getItem('selected-basket-id')
          if (selectedBasketId) {
            const foundBasket = parsedBaskets.find((basket: Basket) => basket.id === selectedBasketId)
            if (foundBasket) {
              setSelectedBasket(foundBasket)
            } else if (parsedBaskets.length > 0) {
              // If stored basket ID not found, select first basket
              setSelectedBasket(parsedBaskets[0])
            }
          } else if (parsedBaskets.length > 0) {
            // If no stored selection, select first basket
            setSelectedBasket(parsedBaskets[0])
          }
        }
      } catch (err) {
        console.error('Error loading baskets from localStorage:', err)
      } finally {
        // Mark that we've completed the loading process
        setHasLoadedFromStorage(true)
      }
    }

    loadBaskets()
  }, [])

  // Save baskets to localStorage whenever baskets change (but not on initial empty state)
  useEffect(() => {
    // Only save after we've loaded from storage to prevent overwriting with initial empty state
    if (hasLoadedFromStorage) {
      try {
        localStorage.setItem('trading-baskets', JSON.stringify(baskets))
        
        // Auto-select first basket if no basket is selected and baskets exist
        if (baskets.length > 0 && !selectedBasket && !isCreatingBasket) {
          setSelectedBasket(baskets[0])
        }
      } catch (err) {
        console.error('Error saving baskets to localStorage:', err)
      }
    }
  }, [baskets, selectedBasket, isCreatingBasket, hasLoadedFromStorage])

  // Save selected basket ID to localStorage whenever it changes
  useEffect(() => {
    try {
      if (selectedBasket) {
        localStorage.setItem('selected-basket-id', selectedBasket.id)
      } else {
        localStorage.removeItem('selected-basket-id')
      }
    } catch (err) {
      console.error('Error saving selected basket to localStorage:', err)
    }
  }, [selectedBasket])

  // Handle price error from React Query
  if (priceError) {
    console.error('Error fetching prices:', priceError)
  }

  // Create a new basket
  const createBasket = useCallback(() => {
    if (!newBasketTitle.trim()) {
      setError('Please enter a basket title')
      return
    }

    const newBasket: Basket = {
      id: Date.now().toString(),
      title: newBasketTitle.trim(),
      pairs: [],
      createdAt: new Date().toISOString()
    }

    setBaskets(prev => [...prev, newBasket])
    setSelectedBasket(newBasket)
    setNewBasketTitle('')
    setIsCreatingBasket(false)
    setError(null)
  }, [newBasketTitle])

  // Add a trading pair to the selected basket
  const addTradingPair = useCallback(() => {
    if (!selectedBasket) {
      setError('Please select a basket first')
      return
    }

    if (!newPairSymbol.trim()) {
      setError('Please enter a symbol')
      return
    }

    const quantity = parseFloat(newPairQuantity)
    if (isNaN(quantity) || quantity <= 0) {
      setError('Please enter a valid quantity')
      return
    }

    // Ensure symbol ends with USDT
    const symbol = newPairSymbol.toUpperCase().endsWith('USDT') 
      ? newPairSymbol.toUpperCase() 
      : `${newPairSymbol.toUpperCase()}USDT`

    // Check if symbol already exists in basket
    if (selectedBasket.pairs.some(pair => pair.symbol === symbol)) {
      setError('This symbol is already in the basket')
      return
    }

    const newPair: TradingPair = {
      id: Date.now().toString(),
      symbol,
      quantity
    }

    const updatedBasket = {
      ...selectedBasket,
      pairs: [...selectedBasket.pairs, newPair]
    }

    setBaskets(prev => prev.map(basket => 
      basket.id === selectedBasket.id ? updatedBasket : basket
    ))
    setSelectedBasket(updatedBasket)
    setNewPairSymbol('')
    setNewPairQuantity('')
    setError(null)
  }, [selectedBasket, newPairSymbol, newPairQuantity])

  // Remove a trading pair from the basket
  const removeTradingPair = useCallback((pairId: string) => {
    if (!selectedBasket) return

    const updatedBasket = {
      ...selectedBasket,
      pairs: selectedBasket.pairs.filter(pair => pair.id !== pairId)
    }

    setBaskets(prev => prev.map(basket => 
      basket.id === selectedBasket.id ? updatedBasket : basket
    ))
    setSelectedBasket(updatedBasket)
  }, [selectedBasket])

  // Delete a basket
  const deleteBasket = useCallback((basketId: string) => {
    setBaskets(prev => prev.filter(basket => basket.id !== basketId))
    if (selectedBasket?.id === basketId) {
      setSelectedBasket(null)
    }
  }, [selectedBasket])

  // Memoized calculation of total USDT required for selected basket
  const totalUsdt = useMemo(() => {
    if (!selectedBasket) return 0
    return selectedBasket.pairs.reduce((total, pair) => {
      const price = prices[pair.symbol]?.last_price || 0
      return total + (pair.quantity * price)
    }, 0)
  }, [selectedBasket, prices])

  // Memoized pair calculations for better performance
  const pairCalculations = useMemo(() => {
    if (!selectedBasket) return []
    return selectedBasket.pairs.map(pair => {
      const price = prices[pair.symbol]?.last_price || 0
      const value = pair.quantity * price
      return {
        ...pair,
        price,
        value
      }
    })
  }, [selectedBasket, prices])

  // Execute bulk buy for the basket
  const executeBulkBuy = async () => {
    if (!selectedBasket || selectedBasket.pairs.length === 0) {
      setError('No trading pairs in the selected basket')
      return
    }

    setIsExecuting(true)
    setError(null)

    try {
      // Get NINX API keys
      const { apiKey, apiSecret } = getNinxKeys()
      
      if (!apiKey || !apiSecret) {
        throw new Error('NINX API keys not found. Please sign in to NINX first.')
      }

      // Execute orders for each pair in the basket with individual error handling
      const orderResults = await Promise.allSettled(
        selectedBasket.pairs.map(async (pair) => {
          try {
            const response = await fetch('/api/connectors/ninx/order', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                apiKey,
                apiSecret,
                order: {
                  symbol: pair.symbol,
                  side: 'BUY',
                  type: 'MARKET',
                  quantity: pair.quantity
                }
              })
            })

            if (!response.ok) {
              const errorData = await response.json()
              throw new Error(`${pair.symbol}: ${errorData.message || 'Unknown error'}`)
            }

            const result = await response.json()
            return { success: true, symbol: pair.symbol, data: result }
          } catch (error) {
            return { success: false, symbol: pair.symbol, error: error instanceof Error ? error.message : 'Unknown error' }
          }
        })
      )

      // Analyze results
      const successful = orderResults.filter(result => result.status === 'fulfilled' && result.value.success)
      const failed = orderResults.filter(result => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success))

      // Provide detailed feedback
      if (successful.length === selectedBasket.pairs.length) {
        alert(`✅ Successfully executed all ${successful.length} buy orders for basket "${selectedBasket.title}"`)
      } else if (successful.length > 0) {
        const failedSymbols = failed.map(result => {
          if (result.status === 'fulfilled') {
            return `${result.value.symbol}: ${result.value.error}`
          }
          return 'Unknown symbol: ' + (result.reason?.message || 'Unknown error')
        }).join('\n')
        
        alert(`⚠️ Partial success: ${successful.length}/${selectedBasket.pairs.length} orders executed.\n\nFailed orders:\n${failedSymbols}`)
      } else {
        const errorMessages = failed.map(result => {
          if (result.status === 'fulfilled') {
            return `${result.value.symbol}: ${result.value.error}`
          }
          return 'Unknown symbol: ' + (result.reason?.message || 'Unknown error')
        }).join('\n')
        
        throw new Error(`All orders failed:\n${errorMessages}`)
      }
      
    } catch (err) {
      console.error('Error executing bulk buy:', err)
      setError(err instanceof Error ? err.message : 'Failed to execute bulk buy')
    } finally {
      setIsExecuting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f6f9fd] p-4">
      <div className=" max-w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#21728d]/10 px-4 py-2 text-sm font-medium text-[#21728d]">
            <ShoppingCart className="h-4 w-4" />
            Trading Baskets
          </div>
          {/* <h1 className="mt-4 text-3xl font-bold text-[#21728d] sm:text-4xl">
            Manage Your Trading Baskets
          </h1> */}
          <p className="mt-2 text-[#88a1ac]">
            Create and manage collections of trading pairs for bulk execution
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4 text-[#cf7381]" />
            <AlertDescription className="text-[#cf7381]">{error}</AlertDescription>
          </Alert>
        )}

        {/* Basket Selection and Creation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#21728d]">
              <ShoppingCart className="h-5 w-5" />
              Your Baskets
            </CardTitle>
            <CardDescription className="text-[#88a1ac]">
              Select an existing basket or create a new one to start trading
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6 flex flex-wrap gap-2">
              {/* Create New Basket Button */}
              <Button
                onClick={() => setIsCreatingBasket(true)}
                variant={isCreatingBasket ? "default" : "outline"}
                className={`flex items-center gap-2 ${
                  isCreatingBasket
                    ? 'bg-[#21728d] text-white hover:bg-[#21728d]/90'
                    : 'border-[#21728d] text-[#21728d] hover:bg-[#21728d]/10'
                }`}
              >
                <Plus className="h-4 w-4" />
                Create New Basket
              </Button>

              {/* Existing Baskets */}
              {baskets.map((basket) => (
                <Badge
                  key={basket.id}
                  variant={selectedBasket?.id === basket.id ? "default" : "secondary"}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    selectedBasket?.id === basket.id
                      ? 'bg-[#21728d] text-white hover:bg-[#21728d]/90'
                      : 'bg-[#88a1ac]/10 text-[#21728d] hover:bg-[#88a1ac]/20'
                  }`}
                  onClick={() => {
                    setSelectedBasket(basket)
                    setIsCreatingBasket(false)
                  }}
                >
                  <span className="mr-2">{basket.title}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteBasket(basket.id)
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>

            {/* Create Basket Form */}
            {isCreatingBasket && (
              <div className="rounded-lg border border-[#88a1ac]/40 bg-[#88a1ac]/10 p-4">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Input
                    placeholder="Enter basket title"
                    value={newBasketTitle}
                    onChange={(e) => setNewBasketTitle(e.target.value)}
                    className="flex-1"
                  />
                  <div className="flex gap-2">
                    <Button onClick={createBasket} className="flex items-center gap-2 bg-[#21728d] text-white hover:bg-[#21728d]/90">
                      <CheckCircle2 className="h-4 w-4" />
                      Create
                    </Button>
                    <Button
                      className="text-[#88a1ac]"
                      variant="outline"
                      onClick={() => {
                        setIsCreatingBasket(false)
                        setNewBasketTitle('')
                        setError(null)
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content */}
        {selectedBasket && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Trading Pairs Management */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#21728d]">
                    <TrendingUp className="h-5 w-5" />
                    Trading Pairs
                  </CardTitle>
                  <CardDescription className="text-[#88a1ac]">
                    Add and manage trading pairs in your basket
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Pair Form */}
                  <div className="rounded-lg border border-[#88a1ac] bg-[#88a1ac]/10 p-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input
                        placeholder="Symbol (e.g., BTCUSDT, ETHUSDT)"
                        value={newPairSymbol}
                        onChange={(e) => setNewPairSymbol(e.target.value)}
                        className="bg-[#88a1ac]/20"
                      />
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="Quantity"
                          value={newPairQuantity}
                          onChange={(e) => setNewPairQuantity(e.target.value)}
                          step="0.00001"
                          min="0"
                          className="flex-1 bg-[#88a1ac]/20"
                        />
                        <Button onClick={addTradingPair} className="flex items-center gap-2 bg-[#21728d] text-white hover:bg-[#21728d]/90">
                          <Plus className="h-4 w-4" />
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Pairs List */}
                  <div className="space-y-2">
                    {selectedBasket.pairs.length === 0 ? (
                      <div className="rounded-lg border-2 border-dashed border-[#88a1ac]/40 bg-white p-8 text-center">
                        <ShoppingCart className="mx-auto h-12 w-12 text-[#88a1ac]" />
                        <h3 className="mt-4 text-lg font-medium text-[#21728d]">
                          No trading pairs yet
                        </h3>
                        <p className="mt-2 text-[#88a1ac]">
                          Add your first trading pair to get started
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Desktop Header - Hidden on mobile */}
                        <div className="hidden md:grid grid-cols-12 gap-2 rounded-lg bg-[#21728d]/10 p-3 text-sm font-medium text-[#21728d]">
                          <div className="col-span-4">Symbol</div>
                          <div className="col-span-2">Quantity</div>
                          <div className="col-span-3">Price</div>
                          <div className="col-span-2">Value</div>
                          <div className="col-span-1">Action</div>
                        </div>

                        {/* Mobile Header */}
                        <div className="md:hidden grid grid-cols-2 gap-2 rounded-lg bg-[#21728d]/10 p-3 text-sm font-medium text-[#21728d]">
                          <div>Trading Pairs</div>
                          <div className="text-right">Actions</div>
                        </div>

                        {/* Pairs */}
                        {pairCalculations.map((pair) => (
                          <div key={pair.id} className="rounded-lg border p-3 hover:bg-slate-50 transition-colors">
                            {/* Desktop Layout */}
                            <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                              <div className="col-span-4">
                                <Badge variant="outline" className="font-mono border-[#21728d]/40 text-[#21728d]">
                                  {pair.symbol}
                                </Badge>
                              </div>
                              <div className="col-span-2 text-sm text-[#88a1ac]">
                                {pair.quantity}
                              </div>
                              <div className="col-span-3">
                                {isLoadingPrices ? (
                                  <Skeleton className="h-4 w-16" />
                                ) : (
                                  <span className={`text-sm font-medium ${
                                    pair.price > 0 ? 'text-[#21728d]' : 'text-[#88a1ac]'
                                  }`}>
                                    {pair.price > 0 ? `$${pair.price.toFixed(6)}` : 'Loading...'}
                                  </span>
                                )}
                              </div>
                              <div className="col-span-2">
                                {isLoadingPrices ? (
                                  <Skeleton className="h-4 w-16" />
                                ) : (
                                  <span className={`text-sm font-medium ${
                                    pair.value > 0 ? 'text-[#21728d]' : 'text-[#88a1ac]'
                                  }`}>
                                    {pair.value > 0 ? `$${pair.value.toFixed(2)}` : '-'}
                                  </span>
                                )}
                              </div>
                              <div className="col-span-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeTradingPair(pair.id)}
                                  className="h-8 w-8 p-0 text-[#21728d] hover:bg-[#21728d]/10 hover:text-[#21728d]"
                                >
                                  <Trash2 className="h-3 w-3 text-[#cf7381]" />
                                </Button>
                              </div>
                            </div>

                            {/* Mobile Layout */}
                            <div className="md:hidden space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline" className="font-mono border-[#21728d]/40 text-[#21728d]">
                                    {pair.symbol}
                                  </Badge>
                                  <span className="text-sm text-[#88a1ac] font-medium">
                                    Qty: {pair.quantity}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeTradingPair(pair.id)}
                                  className="h-8 w-8 p-0 text-[#cf7381] hover:bg-red-50"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-[#88a1ac] block">Price:</span>
                                  {isLoadingPrices ? (
                                    <Skeleton className="h-4 w-20 mt-1" />
                                  ) : (
                                    <span className={`font-medium ${
                                      pair.price > 0 ? 'text-[#21728d]' : 'text-[#88a1ac]'
                                    }`}>
                                      {pair.price > 0 ? `$${pair.price.toFixed(6)}` : 'Loading...'}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <span className="text-[#88a1ac] block">Value:</span>
                                  {isLoadingPrices ? (
                                    <Skeleton className="h-4 w-16 mt-1" />
                                  ) : (
                                    <span className={`font-medium ${
                                      pair.value > 0 ? 'text-[#21728d]' : 'text-[#88a1ac]'
                                    }`}>
                                      {pair.value > 0 ? `$${pair.value.toFixed(2)}` : '-'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Execute Section */}
            {selectedBasket.pairs.length > 0 && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#21728d]">
                      <CheckCircle2 className="h-5 w-5" />
                      Execute Basket
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Balance Required */}
                    <div className="rounded-lg bg-[#88a1ac]/10 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#21728d]">
                          Total Required:
                        </span>
                        <div className="flex items-center gap-2">
                          {isLoadingPrices && <Loader2 className="h-4 w-4 animate-spin" />}
                          <span className="text-lg font-bold text-[#21728d]">
                            ${totalUsdt.toFixed(2)} USDT
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => refetchPrices()}
                            disabled={isLoadingPrices}
                            className="h-8 w-8 p-0 text-[#21728d] hover:text-[#21728d]"
                          >
                            <RefreshCw className={`h-4 w-4 ${isLoadingPrices ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Separator className="bg-[#88a1ac]/40" />

                    {/* Execute Button */}
                    <Button
                      onClick={executeBulkBuy}
                      disabled={isExecuting}
                      className="w-full bg-[#21728d] text-white hover:bg-[#21728d]/90"
                      size="lg"
                    >
                      {isExecuting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Executing Orders...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Execute Bulk Buy
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-[#88a1ac] text-center">
                      This will execute market buy orders for all pairs in your basket
                    </p>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-[#21728d]">Basket Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#88a1ac]">Pairs:</span>
                      <Badge variant="secondary" className="bg-[#88a1ac]/20 text-[#21728d]">
                        {selectedBasket.pairs.length}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#88a1ac]">Created:</span>
                      <span className="text-[#21728d]">
                        {new Date(selectedBasket.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!selectedBasket && !isCreatingBasket && baskets.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ShoppingCart className="h-16 w-16 text-[#88a1ac]" />
              <h3 className="mt-4 text-xl font-semibold text-[#21728d]">
                No baskets yet
              </h3>
              <p className="mt-2 text-[#88a1ac] text-center max-w-md">
                Create your first trading basket to start organizing your trading pairs
                for bulk execution.
              </p>
              <Button
                onClick={() => setIsCreatingBasket(true)}
                className="mt-6 bg-[#21728d] text-white hover:bg-[#21728d]/90"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Basket
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}